import { DerbyRngV1 } from '../rng';
import { DRILL_TICK_S, MASK_HELD, type MinigameDef, type MinigameSession, type SessionScore } from './framework';

export type FlyingPhase = 'charge' | 'release' | 'gap' | 'over';

/** Acceleration timing: hold the horse loaded, then release exactly on GO. */
export class FlyingStartSession implements MinigameSession {
  readonly rounds = 4;
  phase: FlyingPhase = 'charge';
  round = 0;
  phaseTick = 0;
  goTick: number;
  faults = 0;
  lastGrade = 0;
  private heldTicks = 0;
  private scores: number[] = [];
  private readonly rng: DerbyRngV1;

  constructor(readonly tier: number, seed: number) {
    this.rng = new DerbyRngV1(seed).fork('flying-start');
    this.goTick = this.drawGo();
  }

  get finished(): boolean { return this.phase === 'over'; }
  chargeFraction(): number {
    return Math.min(1, this.heldTicks / Math.max(1, this.goTick * 0.8));
  }
  private drawGo(): number { return Math.round(this.rng.range(0.9, 1.8) / DRILL_TICK_S); }
  private finishRound(score: number): void {
    this.lastGrade = score;
    this.scores.push(score);
    this.round += 1;
    if (this.round >= this.rounds) { this.phase = 'over'; return; }
    this.phase = 'gap'; this.phaseTick = 0; this.heldTicks = 0;
  }

  step(mask: number): void {
    if (this.finished) return;
    const held = (mask & MASK_HELD) !== 0;
    if (this.phase === 'charge') {
      if (held) this.heldTicks += 1;
      if (!held && this.heldTicks > 0 && this.phaseTick < this.goTick) {
        this.faults += 1; this.finishRound(0); return;
      }
      this.phaseTick += 1;
      if (this.phaseTick >= this.goTick) { this.phase = 'release'; this.phaseTick = 0; }
      return;
    }
    if (this.phase === 'release') {
      if (!held) {
        const perfect = Math.max(2, 6 - this.tier);
        const reaction = this.phaseTick;
        const timing = reaction <= perfect ? 1 : reaction <= 8 ? 0.75 : reaction <= 13 ? 0.4 : 0.15;
        const charge = this.chargeFraction();
        // Timing without loading the horse is not a start. Previously doing
        // absolutely nothing earned a Silver-quality 0.65 score.
        this.finishRound(timing * charge); return;
      }
      this.phaseTick += 1;
      if (this.phaseTick > 18) { this.finishRound(0.1); return; }
      return;
    }
    if (this.phase === 'gap') {
      this.phaseTick += 1;
      if (this.phaseTick >= 12) {
        this.phase = 'charge'; this.phaseTick = 0; this.goTick = this.drawGo();
      }
    }
  }

  score(): SessionScore {
    const raw = this.scores.length ? this.scores.reduce((a, b) => a + b, 0) / this.scores.length : 0;
    return { raw, detail: `${this.scores.filter((s) => s >= 0.9).length}/${this.rounds} sharp releases · ${this.faults} early`, failed: this.faults >= 3 };
  }
}

export const FLYING_START_DEF: MinigameDef = {
  id: 'flyingStart', name: 'Flying Start', stat: 'accel', energyCost: 4,
  create: (tier, seed) => new FlyingStartSession(tier, seed),
};
